import React, { useEffect, useState } from "react";
import { Box, InputAdornment, Typography } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { SecurityImage } from "@components/SecurityImage";
import { SearchField } from "@components";
import { useSecurityImages } from "@hooks/useSecurityImages";
import { SymbolList, SymbolListContainer } from "./SymbolList";

const Container = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const NoResultsContainer = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
`;

export interface Props {
  search: string;
  handleChange: (search: string) => void;
  showLoadingSkeleton: boolean;
  showNoResults: boolean;
  symbols: SymbolWithQuote[];
  onSelectSymbol: (symbol: SymbolWithQuote) => void;
  isSearchOpen: boolean;
  shouldShowQuote?: boolean;
  onFocus?: () => void;
}

export const SymbolSearchInput = ({
  search,
  handleChange,
  symbols,
  showLoadingSkeleton,
  showNoResults,
  onSelectSymbol,
  isSearchOpen,
  shouldShowQuote,
  onFocus,
}: Props) => {
  const { t } = useTranslation();
  const [currentValue, setCurrentValue] = useState<string>(search);
  const [selectedSymbol, setSelectedSymbolV2] = useState<SymbolWithQuote>();

  const handleSymbolSelect = (symbol: SymbolWithQuote) => {
    onSelectSymbol(symbol);
    setSelectedSymbolV2(symbol);
  };

  const debounced = useDebouncedCallback((value: string) => {
    handleChange(value);
  }, 500);

  useEffect(() => {
    setCurrentValue(search);
  }, [search]);

  const logos = useSecurityImages([
    {
      quote: {
        quoteProvider: selectedSymbol?.quoteProvider,
      },
      hasValidIsin: true,
      type:
        selectedSymbol?.quoteProvider === "currency" ? "81_fcurr" : "11_stock",
      isin: selectedSymbol?.isin || selectedSymbol?.quote?.tickerSymbol,
      ticker_symbol: selectedSymbol?.quote?.tickerSymbol,
      quoteProvider: selectedSymbol?.quoteProvider,
    },
  ]);

  return (
    <Container>
      <SearchField
        fullWidth
        onFocus={onFocus}
        inputProps={{
          "data-testid": "symbol-search-input",
        }}
        label={t("symbolSearch.searchLabel")}
        onChange={(event) => {
          debounced(event.target.value);
          setCurrentValue(event.target.value);
        }}
        placeholder={t("symbolSearch.searchPlaceholder")}
        autoFocus
        size="small"
        id="symbol-search-input"
        value={currentValue}
        InputProps={
          selectedSymbol && !isSearchOpen
            ? {
                startAdornment: (
                  <InputAdornment position="start">
                    <SecurityImage
                      src={
                        logos[selectedSymbol?.isin || ""] ||
                        logos[selectedSymbol?.quote?.tickerSymbol || ""]
                      }
                      width={20}
                      height={20}
                    />
                  </InputAdornment>
                ),
              }
            : undefined
        }
        sx={{
          "& .MuiInputBase-input": {
            paddingLeft: selectedSymbol && !isSearchOpen ? 0 : "12px",
          },
          width: "100%",
        }}
      />

      {showLoadingSkeleton && isSearchOpen && (
        <SymbolListContainer>
          <Box p={3}>{t("symbolSearch.loading")}</Box>
        </SymbolListContainer>
      )}
      {!showNoResults && symbols.length > 0 && isSearchOpen && (
        <SymbolList
          symbols={symbols}
          handleClick={handleSymbolSelect}
          shouldShowQuote={shouldShowQuote}
        />
      )}
      {showNoResults && isSearchOpen && (
        <NoResultsContainer>
          <Typography variant="body1">{t("symbolSearch.noData")}</Typography>
        </NoResultsContainer>
      )}
    </Container>
  );
};
